import React, { FC, HTMLProps, ReactNode } from "react";
import cn from "classnames";
import stl from "./index.module.scss";

interface Props extends Omit<HTMLProps<HTMLButtonElement>, "size"> {
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "inline"
    | "help"
    | "action"
    | "callToAction";
  size?: "small" | "large";
  icon?: ReactNode;
  iconPos?: "before" | "after";
  color?:
    | "primary"
    | "primaryTransparent"
    | "delete"
    | "deleteTransparent"
    | "edit"
    | "editTransparent";
  hideContent?: boolean;
}

const Button: FC<Props> = ({
  variant,
  size,
  className,
  icon,
  iconPos = "before",
  color,
  hideContent,
  ...props
}) => {
  let variantClassName = "";

  switch (variant) {
    case "default": {
      variantClassName = stl.default;
      break;
    }
    case "primary": {
      variantClassName = stl.primary;
      break;
    }
    case "secondary": {
      variantClassName = stl.secondary;
      break;
    }
    case "inline": {
      variantClassName = stl.inline;
      break;
    }
    case "help": {
      variantClassName = stl.help;
      break;
    }
    case "action": {
      variantClassName = stl.action;
      break;
    }
    case "callToAction": {
      variantClassName = stl.callToAction;
      break;
    }
  }

  let sizeClassName = "";

  switch (size) {
    case "small": {
      sizeClassName = stl.small;
      break;
    }
    case "large": {
      sizeClassName = stl.large;
      break;
    }
  }

  const actualProps = {
    tabIndex: 0,
    ...props,
    className: cn(className, variantClassName, sizeClassName, stl.block),
  };

  if (icon) {
    actualProps.children =
      iconPos === "before" ? (
        <>
          <span className={stl.iconWrap}>{icon}</span>
          {!hideContent && props.children}
        </>
      ) : (
        <>
          {!hideContent && props.children}
          <span className={stl.iconWrap}>{icon}</span>
        </>
      );

    actualProps.className = cn(
      actualProps.className,
      iconPos === "before" ? stl.iconBefore : stl.iconAfter,
      { [stl.hideContent]: hideContent }
    );
  }

  if (color) {
    let colorClassName = "";

    switch (color) {
      case "delete": {
        colorClassName = stl.colorDelete;
        break;
      }
      case "deleteTransparent": {
        colorClassName = stl.colorDeleteTransparent;
        break;
      }
      case "edit": {
        colorClassName = stl.colorEdit;
        break;
      }
      case "editTransparent": {
        colorClassName = stl.colorEditTransparent;
        break;
      }
      case "primary": {
        colorClassName = stl.colorPrimary;
        break;
      }
      case "primaryTransparent": {
        colorClassName = stl.colorPrimaryTransparent;
        break;
      }
    }
    actualProps.className = cn(actualProps.className, colorClassName);
  }

  actualProps.role = "button";

  return props.href
    ? React.createElement("a", actualProps)
    : React.createElement("button", actualProps);
};

export default Button;
